import { Component, EventEmitter, Input, OnDestroy, Output,
    OnInit, signal, WritableSignal } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';

import { Subscription, BehaviorSubject, take, combineLatest, of, forkJoin, Observable, timer, throwError } from 'rxjs';
import {
    switchMap,
    catchError,
    tap,
    map as rxjsMap,
    shareReplay,
    finalize,
    mergeMap
} from 'rxjs/operators';
import { xorBy } from 'lodash';
import moment from 'moment-timezone';
import { MenuItem } from 'primeng/api';

import { MediaSubscriptionStatusEnum } from '@Monitor/Shared/enums/media-subscription-status.enum';
import { QuotaUsageRateEnum } from '../../../../../api/mediaSubscriptions/enums/quota-usage-rate.enum';
import { SubscriptionContentTimeslot } from '../../../../../api/mediaSubscriptions/models/subscription-content-timeslot.api.model';

import { ActionKeys } from '../../../../../Common/Forms/enums/keys.enum';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { chartColors } from '../../../../../Common/Charts/enums/chart-colors.enum';
import { ChartStylesModel } from '../../../../../Common/Charts/models/chart-styles.model';
import { ConfirmDialogComponent } from '../../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../../../Common/Dialogs/models/confirm-dialog.model';
import { DateAndValueXYChart } from '../../../../../Common/Charts/models/date-and-value-x-y.chart';
import { DateAndValueXYChartOptionsModel } from '../../../../../Common/Charts/models/date-and-value-x-y-chart-options.model';
import { DropdownOptionModel } from '../../../../../Common/Forms/models/dropdown-option.model';
import { trimLongStringWithEllipsis } from '../../../../../Core/utils';
import { TextFieldModel } from '../../../../../Common/PageBuilder/models/text-field.model';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';

import { MediaSubscriptionService } from '../../../../../Monitor/Core/services/media-subscription.service';

import { AgentService } from '../../../services/agent.service';
import { ThemeService } from '../../../services/theme.service';
import { Theme } from '../../../models/theme.model';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { AgprComponentStyleEnum } from '../../../../../Common/Basic/enums/social-analysis-types.enum';

import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { MediaSubscription } from '../../../../../Monitor/Shared/models/media-subscription.model';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { ManageSocialTopicsDialogComponent } from '../../ManageSocialTopicsDialog/manage-social-topics-dialog.component';

import { AgentWidgetComponent, AgentWidgetMode } from '../../AgentWidget/agent-widget.component';

import { MonitorConfigService } from '../../../../../Common/MonitorShared/services/monitor-config.service';
import { NetworkTypeEnum, mediaTypeToNetwork } from '../../../../Core/enums/network-type.enum';
import { mediaSubTypeToNetworkTypeMap } from '../../../models/network-type-to-media-sub-type.map';
import {
    ContentLibraryPanelFieldComponent } from '../../../../../Common/PageBuilder/components/FormFields/ContentLibraryPanelField/content-library-panel.component';
import { ContentLibraryFieldModel } from '../../../../../Common/PageBuilder/models/content-library-field-model';
import { FILE_SIZE_LIMIT_5MiB } from '../../../../../Core/constants';
import { ChartMetadataModel } from '../../../../../Common/Charts/models/chart-metadata.model';
import { DataPointModel } from '@Common/Charts/models/data-point.model';
import { ContentOverTimeDurationTypeEnum } from '@api/mediaSubscriptions/enums/content-over-time-duration-type.enum';
import { MonitoringQuotaTypesEnum } from '@api/mentions/enum/monitoring-quota-types.enum';
import { MediaSubscriptionApiService } from '@api/mediaSubscriptions/media-subscription.api.service';
import { ResourceActionTypes } from '../../../../../Common/Basic/enums/resource-actions.enum';
import { AgentSourceIndexService } from '../../../../../AutomationAgents/services/agent-source-index.service';
import { ManageSourceAgentsService } from '../../../../../AutomationAgents/services/manage-source-agents.service';
import { MonitoringSeriesTypeEnum } from '../../../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';

/**
 * Future consideration for improving the efficiency of /contentovertime calls
 * Instead of fetching /contentovertime when any agent status changes to or from active in refreshAgentQuota()
 * we can cache that quota data and apply the new computed ingestionRateCorrectedBaseline to it.
 * This would result in less fresh quota data, but if we are truely concerned with that, websocket updates should be considered.
 *
 * The other weird behaviour is since we only fetch data on expand for the first time and we only make updates when the card is expanded,
 * (in the case mentioned above), then on re-exand that quota data won't be accurate. This applies to agent and spark data changes as well.
 */
@Component({
    selector: 'agpr-theme-card',
    templateUrl: './theme-card.component.html',
    styleUrls: ['./theme-card.component.scss'],
    animations: [
        trigger('containerState', [
            state('collapsed', style({
                height: '100px'
            })),
            state('expanded', style({
                height: '{{ cardHeight }}',
            }), { params: { cardHeight: '245px' } }),
            transition('* => collapsed', animate('220ms ease-out')),
            transition('collapsed => expanded', animate('220ms ease-in')),
        ])
    ],
    standalone: false
})
export class ThemeCardComponent implements OnInit, OnDestroy {

    private _theme: Theme;

    @Input() set theme(value: Theme) {
        this._theme = value;
        this.cardHeight = Math.max(
            this.DEFAULT_TABLE_HEIGHT,
            this.TABLE_HEADER_HEIGHT + this.TABLE_ROW_HEIGHT * this.theme.subscriptionIds.length
        ) / 16 + 'rem';
    };

    get theme(): Theme {
        return this._theme;
    }

    @Input() expanded: boolean = false;

    @Output() themeDeleted = new EventEmitter<Theme>(null);
    @Output() themeExpanded = new EventEmitter<boolean>(false);

    themeAgents: MediaSubscription[] = [];

    themeNameClone: string;
    cardHeight: string;
    availableAgentsToAdd: DropdownOptionModel<MediaSubscription>[] = [];

    isEditingName: boolean = false;
    isThemePartiallyModerated: boolean = false;

    /** How many automation agents are live on this theme, and whether the account has them at all */
    liveAgentCount: number = 0;
    hasAgenticFeature: boolean = false;

    private remainingQuota: number;
    private daysRemaining: number;

    private numOfActiveAgents: number;
    private cachedNumActiveAgents: number = null;

    private agents: MediaSubscription[] = [];

    private subscriptions: Subscription[] = [];
    private overlayContainer: Element;

    private sparklineDataProviders = new Map<number, Observable<DataPointModel[]>>();

    // change detection for tracking agent add/remove/status changes
    private currentAgentIds = new Set<number>();
    private agentStatusMap = new Map<number, MediaSubscriptionStatusEnum>();

    // signal maps with agent.id as key
    readonly sparklineDataMap = signal(new Map<number, DateAndValueXYChart>());
    readonly quota24hMap = signal(new Map<number, number>());
    readonly quotaUsageRate = signal(new Map<number, QuotaUsageRateEnum>());

    readonly buttonTypes = ButtonTypesEnum;
    readonly AGENT_MAX = Theme.MAX_NUM_AGENTS;
    readonly DEFAULT_TABLE_HEIGHT = 245;
    readonly TABLE_HEADER_HEIGHT = 60;
    readonly TABLE_ROW_HEIGHT = 45;
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly quotaUsageToIconClassMap = new Map<QuotaUsageRateEnum, string>([
        [ QuotaUsageRateEnum.INACTIVE,        'fa-regular fa-duotone fa-circle'    ],
        [ QuotaUsageRateEnum.SAFE_USAGE,      'fa-regular fa-duotone fa-scrubber'  ],
        [ QuotaUsageRateEnum.HIGH_USAGE,      'fak fa-agpr-circle-high'            ],
        [ QuotaUsageRateEnum.EXCESSIVE_USAGE, 'fa-solid fa-circle-exclamation'     ]
    ]);
    readonly agentOptions: MenuItem[] = [
        {
            label: 'Social.intelligence.themeManagement.themeCard.row.editAgent',
            command: (event: any) => {
                this.openCreateAgentTakeOver(event.data);
                event?.originalEvent?.stopImmediatePropagation();
            }
        },
        {
            label: 'Social.intelligence.themeManagement.themeCard.row.removeAgent',
            command: (event: any) => {
                this.removeAgentFromTheme(event.data);
                event?.originalEvent?.stopImmediatePropagation();
            }
        }
    ];
    readonly contentLibrary = new ContentLibraryFieldModel(
        ContentLibraryPanelFieldComponent,
        'url',
        '',
        {
            maxFiles: 1,
            accepts: 'image/gif, image/jpeg, image/png',
            buttonLabel: 'Social.intelligence.themeManagement.themeCard.editImage',
            headerTooltip: 'Social.intelligence.themeManagement.themeCard.editImage',
            uploadButtonIcon: 'fa-regular fa-duotone fa-pen',
            defaultValue: undefined,
        }
    );
    readonly contentLibraryField = new TextFieldModel(
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        null,
        (value: string) => {
            if (value?.length > FILE_SIZE_LIMIT_5MiB) {
                return this.localizationService.transform('Common.fileSize.limit.message', { size: '5MB' });
            }

            return null;
        }
    );

    constructor(
        private router: Router,
        private coreDialogService: CoreDialogService,
        private localizationService: LocalizationService,
        public agentService: AgentService,
        private themeService: ThemeService,
        private mediaSubscriptionService: MediaSubscriptionService,
        private mediaSubscriptionApiService: MediaSubscriptionApiService,
        private monitorConfigService: MonitorConfigService,
        private agentSourceIndexService: AgentSourceIndexService,
        private currentUserStateService: CurrentUserStateService,
        private manageSourceAgentsService: ManageSourceAgentsService,
    ) {}

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');

        this.subscriptions.push(
            // Automation agents, not the social agents this card lists - a theme can be a source
            // one of them watches, which is marked the same way the monitoring sidebar marks a topic
            this.agentSourceIndexService.liveAgentCounts$.subscribe(counts => this.liveAgentCount =
                counts.get(this.agentSourceIndexService.key(MonitoringSeriesTypeEnum.theme, this.theme.id)) ?? 0),
            this.currentUserStateService.getAgenticFeatureEnabled().subscribe(enabled => this.hasAgenticFeature = enabled),
            this.themeService.getAgentsForTheme(this.theme.id).subscribe(themeAgents => {
                this.themeAgents = themeAgents;
                this.isThemePartiallyModerated = this.themeAgents.some(agent => agent.isPartiallyModerated);

                if (this.expanded) {
                    this.handleAgentChanges(this.themeAgents);
                }

                this.updateAvailableAgents();
            }),
            combineLatest([
                this.agentService.all$,
                this.monitorConfigService.getAvailableSocialMediaTypes()
            ]).subscribe(([agents, mediaTypes]) => {

                if (!agents || !mediaTypes) {
                    return;
                }

                this.agents = agents;

                this.numOfActiveAgents = Math.max(
                    agents.filter(agent => agent.status === MediaSubscriptionStatusEnum.ACTIVE).length, 1
                );

                if (
                    this.expanded &&
                    this.cachedNumActiveAgents != null &&
                    this.numOfActiveAgents !== this.cachedNumActiveAgents
                ) {
                    this.refreshAgentQuota(this.themeAgents);
                }

                this.cachedNumActiveAgents = Math.max(
                    agents.filter(agent => agent.status === MediaSubscriptionStatusEnum.ACTIVE).length, 1
                );

                this.updateAvailableAgents();

                const allowedNetworks = mediaTypes
                    .map(media => mediaTypeToNetwork.get(media))
                    .filter(x => !!x);

                if (allowedNetworks.includes(NetworkTypeEnum.reddit)) {
                    allowedNetworks.push(NetworkTypeEnum.subReddit);
                }

                this.agents.forEach(agent => {
                    agent.mediaTypeRequests = agent.mediaTypeRequests.filter(request =>
                        allowedNetworks.includes(mediaSubTypeToNetworkTypeMap.get(request.mediaType))
                    );
                });
            }),
            this.mediaSubscriptionService.getQuotaInfo(MonitoringQuotaTypesEnum.SOCIAL_LISTENING).subscribe(quotaInfo => {
                if (quotaInfo) {
                    this.remainingQuota = quotaInfo.maxCount + quotaInfo.additionalCount - quotaInfo.currentCount;
                    this.daysRemaining = Math.max(moment(quotaInfo.nextResetDate).diff(moment(), 'days'), 1);
                }
            }),
        );
    }

    toggleThemeCard() {
        this.expanded = !this.expanded;
        this.themeExpanded.emit(this.expanded);
    }

    /**
     * Populate chart & quota data and set tracking state.
     */
    onToggleDone() {
        if (this.expanded) {
            if (this.sparklineDataMap().size === 0) {
                this.generateCharts(this.themeAgents);
                /** @TODO: MER-1018 - new endpoint for ideally all current month quota */
                this.themeAgents.forEach(agent => {
                    this.mediaSubscriptionApiService.getContentOverTime(agent.id, true).pipe(
                        take(1)
                    ).subscribe(quota => this.calculateQuotaUsageRate(agent, quota));
                });

                this.currentAgentIds = new Set(this.themeAgents.map(x => x.id));
                this.agentStatusMap = new Map(this.themeAgents.map(agent => [agent.id, agent.status]));
            } else {
                this.generateCharts(this.themeAgents);
            }
        }
    }


    openManageAgentDialog() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                theme: this.theme
            }
        };
        this.coreDialogService.open(ManageSocialTopicsDialogComponent, io);

    }

    openCreateAgentTakeOver(agent?: MediaSubscription, event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const agentWidgetMode = agent ? AgentWidgetMode.edit : AgentWidgetMode.create;

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                theme: this.theme,
                agentModel: agent,
                mode: agentWidgetMode
            },
            outputs: {}
        };
        this.coreDialogService.open(AgentWidgetComponent, io);
    }


    updateThemeImage(url: string) {
        const clonedTheme = this.theme.clone();
        clonedTheme.avatarUrl = url;

        this.themeService.update(clonedTheme).subscribe();
    }

    editNameClicked() {
        this.themeNameClone = this.theme.name;
        this.isEditingName = true;
    }

    doneEditingName() {
        if (!this.themeNameClone?.trim() || this.themeNameClone?.trim() === this.theme.name) {
            this.isEditingName = false;

            return;
        }

        this.themeNameClone = this.themeNameClone.trim();

        this.themeService.renameDuplicateCheck(this.theme.id, this.themeNameClone).pipe(
            finalize(() => this.isEditingName = false)
        ).subscribe();
    }

    statusChange(status: MediaSubscriptionStatusEnum, agent: MediaSubscription) {
        const previousState = agent.status;
        agent.status = status;

        this.agentService.switchChange(status, agent).subscribe({
            error: () => {
                agent.status = previousState;
            }
        });
    }

    /**
     * Confirm theme deletion, pause solo agents and delete theme
     */
    deleteThemeClicked() {
        const io: ConfirmDialogModel = {
            inputs: {
                id: 'social_themeCard_deleteConfirmDialog',
                heading: 'Social.intelligence.themeManagement.themeCard.deleteTheme',
                message: this.localizationService.transform('Social.intelligence.themeManagement.themeCard.deleteTheme.message', {
                    name: trimLongStringWithEllipsis(this.theme.name, 60)
                }),
                okLabel: 'Social.intelligence.themeManagement.themeCard.deleteTheme.okLabel',
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        soloAgents.forEach(agent => {
                            agent.themeIds = agent.themeIds.filter(id => id !== this.theme.id);
                        });

                        this.themeService.delete(this.theme).pipe(
                            switchMap((id) => {
                                if (soloAgents.length > 0) {
                                    return forkJoin(
                                        soloAgents.map(agent => this.agentService.switchChange(MediaSubscriptionStatusEnum.PAUSED, agent, false))
                                    );
                                } else {
                                    return of(id);
                                }
                            })
                        ).subscribe(() => {
                            this.themeDeleted.emit(this.theme);
                        });
                    }
                }
            }
        };

        // handle special case of last remaining agents
        const soloAgents = this.themeAgents.filter(agent => agent.themeIds.length === 1);
        if (soloAgents.length > 0) {
            io.inputs.message = undefined;
            io.inputs.messageHtml =
                `<div>
                    ${this.localizationService.transform('Social.intelligence.themeManagement.themeCard.deleteTheme.message', { name: this.theme.name })}
                 </div>
                 <br>
                 <div>
                    ${this.localizationService.transform('Social.intelligence.themeManagement.themeCard.deleteTheme.agentWarning')}
                 </div>`;
            io.inputs.bottomMessage = 'Social.intelligence.themeManagement.themeCard.deleteTheme.agentWarning.info';
            io.inputs.list = soloAgents.map(agent => agent.name);
        }

        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    addAgentToTheme(dropdownItem: any) {
        if (dropdownItem.originalEvent instanceof MouseEvent || dropdownItem.originalEvent.key === 'Enter' || dropdownItem.originalEvent.key === ' ') {
            const agent = dropdownItem.value?.value;

            const theme = this.theme.clone();
            theme.subscriptionIds.push(agent.id);
            this.themeService.update(theme).subscribe();
        }
    }

    removeAgentFromTheme(agent: MediaSubscription) {
        const clonedAgent = agent.clone();
        const clonedTheme = this.theme.clone();
        clonedTheme.subscriptionIds = clonedTheme.subscriptionIds.filter(id => id !== clonedAgent.id);
        clonedAgent.themeIds = clonedAgent.themeIds.filter(id => id !== clonedTheme.id);

        if (agent.themeIds.length === 1) {
            /* eslint-disable @stylistic/indent */
            const confirmConfig: ConfirmDialogModel = {
                inputs: {
                    id: 'social.removeAgentConfirmDialog',
                    heading: 'Social.intelligence.themeManagement.themeCard.removeAgent',
                    messageHtml:
                        `<div>
                            ${this.localizationService.transform('Social.intelligence.themeManagement.themeCard.removeAgent.message.1', {
                                agent: trimLongStringWithEllipsis(clonedAgent.name, 60)
                            })}
                        </div>
                        <br>
                        <div>
                            ${this.localizationService.transform('Social.intelligence.themeManagement.themeCard.removeAgent.message.2')}
                        </div>`,
                    okLabel: 'Social.intelligence.themeManagement.themeCard.removeAgent.okLabel',
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.themeService.update(
                                clonedTheme,
                                {
                                    actionType: ResourceActionTypes.UPDATE,
                                    removedAgentName: clonedAgent.name
                                }).pipe(
                                switchMap(() => this.agentService.switchChange(MediaSubscriptionStatusEnum.PAUSED, clonedAgent))
                            ).subscribe();
                        }
                    }
                }
            };
            /* eslint-enable @stylistic/indent */

            this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
        } else {
            this.themeService.update(
                clonedTheme,
                {
                    actionType: ResourceActionTypes.UPDATE,
                    removedAgentName: clonedAgent.name
                }
            ).subscribe();
        }
    }


    cardClicked() {
        if (!this.expanded) {
            this.goToTheme();
        }
    }

    /** Tooltip on the live agent marker - "[n] Live Agent(s)" */
    get liveAgentTooltip(): string {
        return this.localizationService.transform('AutomationAgents.liveAgent', { count: `${this.liveAgentCount}` });
    }

    /** Opens the panel that manages the automation agents watching this theme */
    manageAutomationAgents(event: Event): void {
        event.stopPropagation();

        this.manageSourceAgentsService.open(MonitoringSeriesTypeEnum.theme, this.theme?.id);
    }

    goToTheme() {
        this.router.navigate(['Social/themes/theme', this.theme.id, 'analytics']);
    }

    getChartData(agentId: number): DateAndValueXYChart | undefined {
        return this.sparklineDataMap().get(agentId);
    }

    getQuotaData(agentId: number): number | undefined {
        return this.quota24hMap().get(agentId);
    }

    getQuotaUsageRate(agentId: number): QuotaUsageRateEnum | undefined {
        return this.quotaUsageRate().get(agentId);
    }

    /**
     * Update the spark chart data for the following agent changes
     * - removed agents, destroy
     * - new agents, create
     * - updated agents (status), update
     * @param agents - current agents attached to the theme
     */
    private handleAgentChanges(agents: MediaSubscription[]) {
        const newAgentIds = new Set(agents.map(x => x.id));
        const previousAgentIds = new Set(this.currentAgentIds);

        const previousStatusMap = new Map(this.agentStatusMap);

        const removedAgentIds = Array.from(previousAgentIds)
            .filter(id => !newAgentIds.has(id));

        const addedAgents = agents.filter(agent => !previousAgentIds.has(agent.id));

        const statusChangedAgents = agents.filter(agent => {
            const oldStatus = previousStatusMap.get(agent.id);
            return oldStatus !== undefined && oldStatus !== agent.status;
        });

        // Delete data for removed agents
        if (removedAgentIds.length > 0) {
            this.updateSignalMap(this.sparklineDataMap, (map) => {
                removedAgentIds.forEach(id => map.delete(id));
            });
            this.updateSignalMap(this.quota24hMap, (map) => {
                removedAgentIds.forEach(id => map.delete(id));
            });
            this.updateSignalMap(this.quotaUsageRate, (map) => {
                removedAgentIds.forEach(id => map.delete(id));
            });
        }

        // Add chart & quota data for new agents
        if (addedAgents.length > 0) {
            this.generateCharts(addedAgents);
            this.refreshAgentQuota(addedAgents);
        }

        if (statusChangedAgents.length > 0) {
            this.generateCharts(statusChangedAgents);
        }

        // Update tracking with new state
        this.currentAgentIds = newAgentIds;
        this.agentStatusMap = new Map(agents.map(agent => [agent.id, agent.status]));
    }


    private generateCharts(agents: MediaSubscription[]) {
        this.updateSignalMap(this.sparklineDataMap, (map) => {
            agents.forEach((agent) => map.set(agent.id, this.generateSparklineChart(agent)));
        });
    }

    private refreshAgentQuota(agents: MediaSubscription[]) {
        /** @TODO: MER-1018 - new endpoint for ideally all current month quota */
        agents.forEach(agent => {
            this.mediaSubscriptionApiService.getContentOverTime(agent.id, true).pipe(
                take(1)
            ).subscribe(quota => this.calculateQuotaUsageRate(agent, quota));
        });
    }

    /**
     * Generate fresh chart instances so agpr-chart renders the chart properly.
     * Leverages getOrCreateSparkProvider to memoize data providers, preventing
     * redundant network calls on re-expansion
     */
    private generateSparklineChart(agent: MediaSubscription): DateAndValueXYChart {
        const series = [{
            label: 'usage',
            key: 'usage',
            dataProvider: this.getOrCreateSparkProvider(agent),
        }];

        const chartStyles = new ChartStylesModel();
        chartStyles.chartDisplaySettings.title = false;
        chartStyles.chartDisplaySettings.dateRange = false;
        chartStyles.chartDisplaySettings.sourceLink = false;

        if (agent.status === MediaSubscriptionStatusEnum.ACTIVE) {
            chartStyles.colorMap['usage'] = chartColors.agprBlue;
            chartStyles.strokeDasharray = '';
        } else {
            chartStyles.colorMap['usage'] = chartColors.agprGreyDark58;
            chartStyles.strokeDasharray = '4, 3';
        }

        const options: DateAndValueXYChartOptionsModel = {
            styles: chartStyles,
            isSparkline: true,
            dateGranularity: {
                timeUnit: 'hour',
                count: 1
            }
        };

        const metadata: ChartMetadataModel = {
            type: null,
            titleText: null,
            dateRange: null,
            dateRangeLabelText: null,
            labelDisplayOptions: [],
            enableDataDisplaySettings: false,
            noContentLabel: this.localizationService.transform('Monitoring.result.noContentFound')
        };

        return new DateAndValueXYChart(series, options, metadata);
    }

    /**
     * Generate available agents options that can be added to the theme
     */
    private updateAvailableAgents() {
        requestAnimationFrame(() => {
            const options: MediaSubscription[] = xorBy(this.agents, this.themeAgents, 'id');
            this.availableAgentsToAdd = options
                .map(x => new DropdownOptionModel<MediaSubscription>(x.name, x))
                .sort((a, b) => a.label.localeCompare(b.label));
        });
    }

    /**
     * Relies on the follow variables that are set in subscriptions
     * - `remainingQuota`, `daysRemaining` (mediaSubscriptionService.getQuotaInfo())
     * - `numOfActiveAgents` (this.agentService.all$)
     */
    private calculateQuotaUsageRate(agent: MediaSubscription, quota: SubscriptionContentTimeslot[]) {
        if (
            agent.status === MediaSubscriptionStatusEnum.STAND_BY
            || agent.status === MediaSubscriptionStatusEnum.PAUSED
            || agent.status === MediaSubscriptionStatusEnum.MODERATED
        ) {
            this.updateSignalMap(this.quotaUsageRate, (map) => map.set(agent.id, QuotaUsageRateEnum.INACTIVE));

            return;
        }

        let totalUsage = 0, daysOfUsage = 0;
        quota.forEach(quo => {
            totalUsage += quo.count;
            if (quo.count > 0) {
                daysOfUsage++;
            }
        });
        // avoid divided by zero exception, assuming no previous quota usage is safe usage
        if (daysOfUsage === 0) {
            this.updateSignalMap(this.quotaUsageRate, (map) => map.set(agent.id, QuotaUsageRateEnum.SAFE_USAGE));

            return;
        }
        const ingestionRate = totalUsage / daysOfUsage;
        // negavtive remaining quota also works
        const ingestionRateCorrectedBaseline = this.remainingQuota / this.numOfActiveAgents / this.daysRemaining;

        if (ingestionRate >= ingestionRateCorrectedBaseline) {
            this.updateSignalMap(this.quotaUsageRate, (map) => map.set(agent.id, QuotaUsageRateEnum.EXCESSIVE_USAGE));
        } else if (ingestionRate >= ingestionRateCorrectedBaseline * 0.8) {
            this.updateSignalMap(this.quotaUsageRate, (map) => map.set(agent.id, QuotaUsageRateEnum.HIGH_USAGE));
        } else {
            this.updateSignalMap(this.quotaUsageRate, (map) => map.set(agent.id, QuotaUsageRateEnum.SAFE_USAGE));
        }
    }

    private getOrCreateSparkProvider(agent: MediaSubscription): Observable<DataPointModel[]> {
        if (!this.sparklineDataProviders.has(agent.id)) {
            this.sparklineDataProviders.set(agent.id, this.getSparkProvider(agent));
        }
        return this.sparklineDataProviders.get(agent.id);
    }

    private getSparkProvider(agent: MediaSubscription): Observable<DataPointModel[]> {
        /** @TODO: MER-1018 - new endpoint for ideally all last 24 hour quota */
        return this.mediaSubscriptionApiService.getContentOverTime(
            agent.id, true, ContentOverTimeDurationTypeEnum.LAST_24_HOURS, 1
        ).pipe(
            tap(response => {
                // sum slices to get past 24 hour quota usage
                const quota24h = response.reduce((a, b) => a + b.count, 0);
                this.updateSignalMap(this.quota24hMap, (map) => map.set(agent.id, quota24h));
            }),
            rxjsMap<SubscriptionContentTimeslot[], DataPointModel[]>(
                response => {
                    return this.prepareSparklineChartData(response);
                }
            ),
            shareReplay(1),
            catchError(error => {
                if (error.status === 429) {
                    this.updateSignalMap(this.quota24hMap, (map) => map.set(agent.id, 0));
                    // show chart of all zeros instead of "no data to display"
                    return of(this.prepareSparklineChartData([]));
                }
            })
        );
    }

    private prepareSparklineChartData(data: SubscriptionContentTimeslot[]): DataPointModel[] {
        const dataPoints = [];
        if (data?.length) {
            for (let i = 0; i < data.length; i++) {
                // only show the last bullet
                const disabled = i !== data.length - 1;
                const dataPoint = {
                    xValue: moment(data[i].to).valueOf(),
                    yValue: data[i].count,
                    mentionCount: data[i].count,
                    label: '',
                    bulletDisabled: disabled
                };
                dataPoints.push(dataPoint);
            }
        } else {
            // Add all zero counts for the last 24hrs for the sparkchart
            let currentTime = this.getStartTime();
            const endTime = this.getEndTime();

            for (; currentTime <= endTime; currentTime = moment(currentTime).add(1, 'hours').toDate().getTime()) {
                const dataPoint = {
                    xValue: moment(currentTime).valueOf(),
                    yValue: 0,
                    mentionCount: 0,
                    label: '',
                    bulletDisabled: currentTime < endTime
                };
                dataPoints.push(dataPoint);
            }
        }

        return dataPoints;
    }

    /**
     * Helper to update Map signals with proper change detection.
     * Creates a new Map reference to ensure Angular detects changes.
     * Used throughout this component for all Map mutations.
     */
    private updateSignalMap<K, V>(
        mapSignal: WritableSignal<Map<K, V>>,
        operation: (map: Map<K, V>) => void
    ) {
        mapSignal.update(map => {
            const newMap = new Map(map);
            operation(newMap);
            return newMap;
        });
    }

    private getStartTime(): number {
        const start = moment(moment.now());
        start.add(-24, 'hours');

        return start.toDate().getTime();
    }

    private getEndTime(): number {
        const end = moment(moment.now());
        end.add(0, 'hours');

        return end.toDate().getTime();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.sparklineDataMap.set(new Map());
        this.quota24hMap.set(new Map());
        this.quotaUsageRate.set(new Map());
    }
}
